import {
  Cake,
  HelpCenter as HelpCenterIcon,
  Info,
  InsertLink,
  Mail,
  OpenInNew,
  Phone,
  Warning,
  Wc,
} from '@mui/icons-material';
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  SxProps,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Marker, useJsApiLoader } from '@react-google-maps/api';
import { FormikErrors, useFormik } from 'formik';
import { ChangeEvent, CSSProperties, useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import usePlacesService from 'react-google-autocomplete/lib/usePlacesAutocompleteService';
import {
  PractitionerWrapper,
  WrappedPractitioner,
} from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Practitioner';
import { DirectoryProviderLookUp } from 'src/@types/crs/providerDirectory';
import { fhirClient } from 'src/App';
import CustomModal, {
  CustomModalBasicProps,
  GridItem,
  GridSection,
} from 'src/components/CustomModal';
import useNotiMessage from 'src/hooks/useNotiMessage';
import useTranslations from 'src/careflow/ph-core/internationalization/hook/useTranslations';
import {
  Address,
  Bundle,
  Coding,
  Extension,
  Practitioner,
} from 'src/careflow/ph-core/fhir-client/resources';
import CancelButton from 'src/careflow/common/CancelButton';
import {
  CustomLoadingButton,
  CustomLoadingButtonProps,
} from 'src/careflow/common/CustomButton';
import {
  iconGreyFillColor,
  LOOKUP_ERROR_MESSAGE_CODES,
  NPI_IDENTIFIER_SYSTEM,
} from 'src/careflow/helpers/constants';
import AddressAutoComplete, {
  PlaceType,
} from 'src/careflow/care-coordination/referral/components/workflow-step/AddressChecklistItem/AddressAutoComplete';
import Map, {
  getCity,
  getLine,
  getPostalCode,
  getState,
} from 'src/careflow/care-coordination/referral/components/workflow-step/AddressChecklistItem/Map';
import SearchCombinationsDialog from '../../new/SearchCombinationsDialog';

import { DataGrid, GridColDef } from '@mui/x-data-grid';
import moment from 'moment';
import { useNavigate } from 'react-router';
import SeverityStatus, { AlertSeverity } from 'src/components/SeverityStatus';
import useAuth from 'src/careflow/ph-core/authentication/hook/useAuth';
import useTenantConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantConfigData';
import { CRS_PATH } from 'src/routes/paths';
import CellRow from 'src/careflow/common/CellRow';
import { getFormattedPersonDOB } from 'src/careflow/YoCare/utils/personHelpers';
import { getResourceUniqueIdentifierPayload } from 'src/careflow/ph-core/fhir-client/util/fhirUtil';
import { formatUSAddress } from 'src/utils/address';
import { spreadSxProp } from 'src/utils/cssStyles';
import crsAcls from 'src/careflow/ph-core/admin/permissions/util/crs/crsAcls';
import { checkAclValidation } from 'src/careflow/ph-core/admin/permissions/util/permission.utils';
import { capitalize, containsSpecialCharacters, isValidEmail } from 'src/utils/string';
import uuidv4 from 'src/utils/uuidv4';
import StethoscopeIcon from 'src/assets/icons/stethoscope.svg';
import {
  getGenderFromProvider,
  getPractitionersLinkedToProvider,
  mapProviderAddressToFhir,
  mapProviderDirectoryToFhirPractitioner,
  mapProviderSearchFieldsToFhirPractitioner,
} from '../../helpers';
import ExistingPractitionerDialog from '../../new/ExistingPractitionerDialog';
import { PractitionerSearchFields } from '../../types';
import '../styles.css';
import Iconify from 'src/components/Iconify';
import { PractitionerService } from 'src/careflow/care-coordination/practitioner/provider/PractitionerService';

export interface NewPractitionerModalProps extends CustomModalBasicProps { 
  configurations: any, 
  handlerActionCreate?: Function 
}

export interface PractitionerGrid {
  id: string;
  npi: string;
  firstName: string;
  middleName: string;
  lastName: string;
  phone: string;
  email: string;
  address: Address | null;
  foundAt: 'localDb' | 'directory';
  gender: string;
  birthDate: moment.Moment;
}

const iconSize: CSSProperties = { height: 15, width: 15 };
const gridCellFontSize: SxProps = { fontSize: '0.9rem' };

const NewPractitionerModal = ({ configurations, handlerActionCreate, open, onClose, ...modalProps }: NewPractitionerModalProps) => {
  const translate = useTranslations(`practitioner`);
  const queryClient = useQueryClient();

  const form = useFormik<PractitionerSearchFields>({
    initialValues: {
      npi: '',
      firstName: '',
      lastName: '',
      phone: '',
      email: '',
      address: '',
      street1: '',
      street2: '',
      city: '',
      state: '',
      zipCode: '',
      shouldLookForPracticeNPI: false,
    },
    onSubmit: (values) => {},
  });

  const user = useAuth();
  const [practitioners, setPractitioners] = useState<PractitionerGrid[]>([]);
  const [isDoneSearching, setIsDoneSearching] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isInvalidCombinationError, setIsInvalidCombinationError] = useState(false);
  const [showAddressFields, setShowAddressFields] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState<PlaceType | null>(null);
  const [isSearchCombinationDialogOpen, setIsSearchCombinationDialogOpen] = useState(false);
  const [searchCombinations, setSearchCombinations] = useState<string[]>([]);
  const { showErrorMessage, showSuccessMessage } = useNotiMessage();
  const [isAdding, setIsAdding] = useState(false);
  const [isExistingPractitionersDialogOpen, setIsExistingPractitionersDialogOpen] = useState(false);
  const [existingPractitioners, setExistingPractitioners] = useState<Practitioner[]>([]);
  const [addressExtension, setAddressExtension] = useState<Extension | null>(null);
  const { componentsData } = useTenantConfigData();
  const [useExternalProviderDirectory, setUseExternalProviderDirectory] = useState(false);
  const [resultsMoreThanLimit, setResultsMoreThanLimit] = useState(false);
  const [pageSize, setPageSize] = useState<number>(10);

  const { practitioner: practitionerTenantData } = componentsData ?? {};

  const navigate = useNavigate();

  const [markerPosition, setMarkerPosition] = useState<{
    lat: number | null;
    lng: number | null;
  }>({
    lat: null,
    lng: null,
  });

  const JsOptions = useMemo<any>(
    () => ({
      id: 'google-map-script',
      googleMapsApiKey: configurations?.googleMapsApiKey || '',
      libraries: ['places'],
    }),
    []
  );
  
  const { isLoaded: isGoogleAPILoaded } = useJsApiLoader(JsOptions);

  const { placesService } = usePlacesService({
    apiKey: configurations?.googleMapsApiKey
  });

  const handleFieldValueChange: React.ChangeEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      setErrorMessage('');

      form.handleChange(event);
    },
    []
  );

  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setShowMap(false);
        setShowAddressFields(false);
        setIsDoneSearching(false);
        setIsInvalidCombinationError(false);
        setIsExistingPractitionersDialogOpen(false);
        setAddressExtension(null);
        setSelectedAddress(null);
        setErrorMessage('');
        form.resetForm();
      }, 400);
    }
  }, [open]);

  useEffect(() => {
    if(!isGoogleAPILoaded) return;
    if (selectedAddress !== null) {
      placesService?.getDetails(
        {
          placeId: selectedAddress.place_id,
        },
        (place) => {
          const address = place?.address_components ?? [];
          form.setValues({
            ...form.values,
            street1: getLine(address),
            city: getCity(address) ?? '',
            state: getState(address) ?? '',
            zipCode: getPostalCode(address) ?? '',
          });
          setMarkerPosition({
            lat: place?.geometry?.location?.lat?.() ?? null,
            lng: place?.geometry?.location?.lng?.() ?? null,
          });
          setAddressExtension({
            extension: [
              {
                url: 'identifier',
                valueString: place?.place_id,
              },
              {
                url: 'valid',
                valueBoolean: true,
              },
              {
                url: 'address',
                valueString: getLine(address),
              },
              {
                url: 'latitude',
                valueDecimal: place?.geometry?.location?.lat?.(),
              },
              {
                url: 'longitude',
                valueDecimal: place?.geometry?.location?.lng?.(),
              },
            ],
            url: `${configurations?.fhir?.baseUrl}/StructureDefinition/PH-address-valid`,
          });
        }
      );
    }
  }, [selectedAddress, isGoogleAPILoaded]);

  useEffect(() => {
    const getSearchCombinations = async () => {
      if (!practitionerTenantData?.providerDirectoryURL) return;
      const response =
        (await PractitionerService.lookupPractitionersInDirectory({
          npi: '',
          first_name: 'dummy',
          last_name: '',
          address: '',
          city: '',
          classification: '',
          practice_name: '',
          practice_npi: '',
          phone_number: '',
        })) ?? {};
      if (
        response?.error_code === LOOKUP_ERROR_MESSAGE_CODES.INVALID_VALUE_SET &&
        response?.error_message?.message
      )
        setSearchCombinations(
          Array.isArray(response.error_message.message)
            ? response.error_message.message
            : [response.error_message.message]
        );
    };
    getSearchCombinations();
  }, [practitionerTenantData]);

  const handleProviderSearch = async () => {
    try {
      const {
        firstName,
        lastName,
        npi,
        address,
        city,
        email,
        phone,
        street1,
        street2,
        state,
        zipCode,
        shouldLookForPracticeNPI,
      } = form.values;
      if (
        !Object.entries(form.values)
          .reduce<boolean[]>((values, [key, value]) => {
            if (key === 'shouldLookForPracticeNPI') return values;
            return [...values, !!value.trim()];
          }, [])
          .includes(true)
      )
        return setErrorMessage(translate('errorMessage.specifyAtLeastOneCriteria'));

      setIsSearching(true);

      const providerResponse =
        practitionerTenantData?.providerDirectoryURL && useExternalProviderDirectory
          ? (await PractitionerService.lookupPractitionersInDirectory(
              {
                npi,
                first_name: firstName,
                last_name: lastName,
                address,
                city,
                classification: '',
                practice_name: '',
                practice_npi: shouldLookForPracticeNPI ? npi : '',
                phone_number: phone,
              },
              practitionerTenantData.providerDirectoryURL
            )) ?? {}
          : null;

      const practitionerFilters = {
        ...(npi.trim()
          ? {
              identifier: `${NPI_IDENTIFIER_SYSTEM}|${npi.trim()}`,
            }
          : {}),

        ...(firstName.trim()
          ? {
              given: firstName.trim(),
            }
          : {}),
        ...(lastName.trim()
          ? {
              family: lastName.trim(),
            }
          : {}),
        ...(phone.trim()
          ? {
              phone: phone.trim(),
            }
          : {}),
        ...(email.trim()
          ? {
              email: email.trim(),
            }
          : {}),
        ...(city.trim()
          ? {
              'address-city': city.trim(),
            }
          : {}),
        ...(state.trim()
          ? {
              'address-state': state.trim(),
            }
          : {}),
        ...(zipCode.trim()
          ? {
              'address-postalcode': zipCode.trim(),
            }
          : {}),
        ...(street1.trim() || street2.trim()
          ? {
              address: `${street1.trim()} ${street2.trim()}`.trim(),
            }
          : {}),
      };

      const practitioners = await fhirClient.search(
        `Practitioner?${new URLSearchParams(practitionerFilters).toString()}`
      );
      let practitionersList = [
        ...(practitioners?.entry?.map?.(({ resource }) =>
          resource ? PractitionerWrapper(resource as Practitioner) : {}
        ) ?? []),
        ...(providerResponse?.matches ?? []),
      ].map((practitioner) =>
        mapPractitionerToPractitionerGrid(practitioner as WrappedPractitioner)
      );

      if (
        !practitionersList.length &&
        providerResponse?.error_message?.message &&
        providerResponse?.error_code === LOOKUP_ERROR_MESSAGE_CODES.INVALID_VALUE_SET
      ) {
        setIsInvalidCombinationError(true);
      }

      // setResultsMoreThanLimit(false);
      // show only first 25
      // TODO: find a way to show more than 25 wihtout making the app unresponsive
      // if (practitionersList.length > 25) {
      //   setResultsMoreThanLimit(true);
      //   practitionersList = practitionersList.slice(0, 25);
      // }

      setPractitioners(practitionersList);
      setShowMap(false);
      setIsDoneSearching(true);
    } catch (error) {
      showErrorMessage(translate('errorMessage.unableToRetrievePractitioners'));
    }
    setIsSearching(false);
  };

  const handleNavigateToPractitionerDetails = (practitionerId: string) => {
    navigate(`${CRS_PATH.provider.root}/${practitionerId}`);
    showSuccessMessage(translate('practitionerCreated'));
  };

  const handleShowExistingPractitioners = (existingPractitioners: Practitioner[]) => {
    setExistingPractitioners(existingPractitioners);
    setIsExistingPractitionersDialogOpen(true);
  };

  const validateManualCreationFields = () => {
    const { firstName, lastName, phone, email, street1, city, state, zipCode } = form.values;
    const errors: FormikErrors<PractitionerSearchFields> = {};

    if (containsSpecialCharacters(firstName, /[\d.!@#$%^&*()_+\-=\[\]{};':"\\|,<>\?/]/))
      errors.firstName = translate('errorMessage.firstName.noSpecialCharacters');
    if (!firstName.trim()) errors.firstName = translate('errorMessage.firstName.isRequired');
    if(containsSpecialCharacters(firstName, /[\s]/)) 
      errors.firstName = translate('errorMessage.firstName.emptySpace');

    if (containsSpecialCharacters(lastName, /[\d.!@#$%^&*()_+\=\[\]{};':"\\|,<>\?/]/ ))
      errors.lastName = translate('errorMessage.lastName.noSpecialCharacters');
    if (!lastName.trim()) errors.lastName = translate('errorMessage.lastName.isRequired');
    if(containsSpecialCharacters(lastName, /[\s]/)) 
      errors.lastName = translate('errorMessage.lastName.emptySpace');

    if (!phone.trim() && practitionerTenantData?.requiredPhone) errors.phone = translate('errorMessage.phone.isRequired');

    if (email.trim() && !isValidEmail(email))
      errors.email = translate('errorMessage.email.mustBeValidEmail');
    if (!email.trim() && practitionerTenantData?.requiredEmail) errors.email = translate('errorMessage.email.isRequired');

    if (practitionerTenantData?.requiredAddress) {
      const hasAddress = street1.trim() || city.trim() || state.trim() || zipCode.trim();
      if (!hasAddress) {
        errors.street1 = translate('errorMessage.address.isRequired');
      }
    }

    if (Object.entries(errors).length) {
      form.setErrors(errors);
      return false;
    }

    return true;
  };

  function handleUseExternalProviderDirectory(
    event: ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) {
    setUseExternalProviderDirectory(checked);
  }

  const handleManualAdd = async () => {
    if (!validateManualCreationFields()) return;
    setIsAdding(true);

    let practitionerPayload: Practitioner;

    practitionerPayload = mapProviderSearchFieldsToFhirPractitioner(
      { ...form.values, addressExtension: addressExtension },
      user?.getCurrentUser?.()?.email,
      configurations?.provider
    );

    const practitionerIdentifier =
      (await getResourceUniqueIdentifierPayload(practitionerPayload.resourceType)) ?? undefined;

    if (practitionerIdentifier) {
      practitionerPayload = {
        ...practitionerPayload,
        identifier: practitionerPayload.identifier?.length
        ? [...practitionerPayload.identifier, practitionerIdentifier]
        : undefined
      };
    }

    handleAddPractitioner(practitionerPayload);
  };

  const handleAddPractitioner = async (payload: Practitioner) => {
    try {
      setIsAdding(true);
      // Use backend API to   create practitioner with automatic identifier (like Tasks)
      const practitioner = (await PractitionerService.createPractitioner({
        practitioner: payload
      })) as Practitioner;

      if (!practitioner) throw new Error('Error Creating Practitioner');
      
      await queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'Practitioner' || 
                             query.queryKey[0] === 'PractitionerRole'
      });
      
      if(handlerActionCreate){
        handlerActionCreate(practitioner);
      }
      else
        handleNavigateToPractitionerDetails(practitioner.id as string);
    } catch (error) {
      showErrorMessage(translate('errorMessage.unableToCreatePractitioner'));
    }
    setIsAdding(false);
  };

  const handleCreatePractitionerFromProvider = async (providerId: string) => {
    setIsAdding(true);
    try {
      const existPractitioners = await getPractitionersLinkedToProvider(
        providerId, configurations?.provider
      );
      if (existPractitioners.length) {
        setIsAdding(false);
        return handleShowExistingPractitioners(existPractitioners);
      }
    } catch (error) {
      setIsAdding(false);
      return showErrorMessage(translate('errorMessage.errorWhileVerifyingExistingPractitioners'));
    }

    const providerDetails = await PractitionerService.getProviderDetails(
      providerId,
      practitionerTenantData?.providerDirectoryURL
    );
    if (!providerDetails) {
      setIsAdding(false);
      return showErrorMessage(translate('errorMessage.errorGettingProviderDetails'));
    }

    let fhirIdentifierTypes: Coding[] | null = null;
    let licenseCodeMapping: any = null;

    try {
      const providerMappingData = 
        await PractitionerService.getProviderDirectoryMappingData(configurations?.fhir?.terminologyUrl);

      if (!providerMappingData.fhirIdentifierTypes || !providerMappingData.licenseCodeMapping)
        throw new Error('Missing Provider Directory Mapping Data');

      fhirIdentifierTypes = providerMappingData.fhirIdentifierTypes;
      licenseCodeMapping = providerMappingData.licenseCodeMapping;
    } catch (error) {
      setIsAdding(false);
      return showErrorMessage(translate('errorMessage.missingProviderDirectoryMappingData'));
    }

    const practitionerPayload = mapProviderDirectoryToFhirPractitioner({
      provider: providerDetails,
      userName: user?.getCurrentUser?.()?.email,
      fhirIdentifierTypes,
      licenseCodeMapping,
      configurations: configurations?.provider
    });
    handleAddPractitioner(practitionerPayload);
  };

  const gridColumns: GridColDef<PractitionerGrid>[] = [
    {
      flex: 1.3,
      sortable: false,
      field: 'npi',
      headerName: translate('npi'),
      renderCell: (params) => {
        const practitioner = params.row;
        return (
          <CellRow
            shouldTruncateText={false}
            title={practitioner.npi}
            titleSx={{ ...gridCellFontSize }}
          />
        );
      },
    },
    {
      flex: 2,
      sortable: false,
      field: 'details',
      headerName: translate('details'),
      renderCell: (params) => {
        const practitioner = params.row;
        return (
          <Box>
            <CellRow
              shouldTruncateText={false}
              titleSx={{ ...gridCellFontSize }}
              title={capitalize(
                `${practitioner.firstName} ${practitioner.middleName} ${practitioner.lastName}`
              )}
              Icon={
                practitionerTenantData?.list?.practitionerName?.icon ? (
                  <Iconify 
                    icon={practitionerTenantData.list.practitionerName.icon} 
                    sx={{
                      ...iconSize,
                      color: '#637381'
                    }} 
                  />
                ) : (
                  <StethoscopeIcon
                    style={{
                      ...iconSize
                    }}
                  />
                )
              }
            />

            <CellRow
              tooltipTitle={translate('gender')}
              shouldTruncateText={false}
              titleSx={{ ...gridCellFontSize }}
              title={capitalize(practitioner.gender)}
              Icon={<Wc sx={{ ...iconSize }} htmlColor={iconGreyFillColor} />}
            />
            <CellRow
              tooltipTitle={translate('dob')}
              shouldTruncateText={false}
              titleSx={{ ...gridCellFontSize }}
              title={getFormattedPersonDOB(practitioner.birthDate.toISOString())}
              Icon={
                <Cake
                  sx={{ ...iconSize, top: -2, position: 'relative' }}
                  htmlColor={iconGreyFillColor}
                />
              }
            />
          </Box>
        );
      },
    },
    {
      flex: 2,
      sortable: false,
      field: 'contactDetails',
      headerName: translate('contactDetails'),
      renderCell: (params) => {
        const practitioner = params.row;

        return (
          <Box>
            <CellRow
              tooltipTitle={translate('phone')}
              shouldTruncateText={false}
              title={practitioner.phone}
              titleSx={{ ...gridCellFontSize }}
              Icon={<Phone sx={{ ...iconSize }} htmlColor={iconGreyFillColor} />}
            />
            <CellRow
              tooltipTitle={translate('email')}
              shouldTruncateText={false}
              titleSx={{ ...gridCellFontSize }}
              title={practitioner.email}
              Icon={<Mail sx={{ ...iconSize }} htmlColor={iconGreyFillColor} />}
            />
          </Box>
        );
      },
    },
    {
      flex: 2,
      sortable: false,
      field: 'addresss',
      headerName: translate('address'),
      renderCell: (params) => {
        const practitioner = params.row;

        return (
          <CellRow
            tooltipTitle={translate('address')}
            shouldTruncateText={false}
            titleSx={{
              whiteSpace: 'pre-line',
              lineHeight: 1.5,
              ...gridCellFontSize,
            }}
            title={practitioner.address ? formatUSAddress(practitioner.address) : null}
          />
        );
      },
    },
    {
      flex: 1,
      sortable: false,
      field: 'foundAt',
      headerName: translate('foundAt.title'),
      renderCell: (params) => {
        const practitioner = params.row;

        return (
          <SeverityStatus
            status={{
              message: translate(`foundAt.${practitioner.foundAt}`),
              severity:
                practitioner.foundAt === 'directory' ? AlertSeverity.PURPLE : AlertSeverity.SUCCESS,
            }}
          />
        );
      },
    },
    {
      flex: 2,
      field: 'action',
      sortable: false,
      align: 'center',
      headerName: ' ',
      renderCell: (params) => {
        const practitioner = params.row;
        const isDirectory = practitioner.foundAt === 'directory';
        if (isDirectory && !checkAclValidation({ acls: [crsAcls.CRS.PRACTITIONER.ADD] }))
          return null;

        if (!isDirectory && !checkAclValidation({ acls: [crsAcls.CRS.PRACTITIONER.VIEW] }))
          return null;
        return (
          <CustomLoadingButton
            sx={{ whiteSpace: 'nowrap' }}
            disabled={isAdding}
            loading={isAdding}
            onClick={() => {
              if (isDirectory) return handleCreatePractitionerFromProvider(practitioner.id);
              window.open(`${CRS_PATH.provider.root}/${practitioner.id}`, '_blank');
            }}
            startIcon={isDirectory ? <InsertLink /> : <OpenInNew />}
          >
            {isDirectory ? translate('createAndLink') : translate('viewProfile')}
          </CustomLoadingButton>
        );
      },
    },
  ];

  return (
    <>
      <CustomModal
        open={open}
        title={translate('newPractitionerTitle')}
        breadcrumbs={[]}
        onSave={() => {}}
        showSaveButton={false}
        showCancelButton={false}
        onCancel={onClose as Function}
        onClose={onClose}
        containerSx={[{ width: '90vw', minHeight: '54vh' }]}
        {...modalProps}
      >
        <Grid container sx={{ width: '100%', height: '100%' }} my={3} spacing={2}>
          <Grid item xs={5}>
            <GridSection mt={0}>
              <GridItem>
                <TextField
                  fullWidth
                  id="npi"
                  name="npi"
                  label={translate('npi')}
                  placeholder={translate('npi')}
                  value={form.values.npi}
                  onChange={handleFieldValueChange}
                />
              </GridItem>
              <GridItem sx={{ display: 'flex', alignItems: 'center' }}>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={form.values.shouldLookForPracticeNPI}
                        name="shouldLookForPracticeNPI"
                        id="shouldLookForPracticeNPI"
                        color="primary"
                        onChange={form.handleChange}
                      />
                    }
                    label={translate('lookForPracticeNPI')}
                  />
                </FormGroup>
              </GridItem>
            </GridSection>
            <GridSection>
              <GridItem>
                <TextField
                  fullWidth
                  id="firstName"
                  name="firstName"
                  label={translate('firstName')}
                  placeholder={translate('firstName')}
                  error={!!form.errors.firstName}
                  helperText={form.errors.firstName}
                  value={form.values.firstName}
                  onChange={handleFieldValueChange}
                />
              </GridItem>
              <GridItem>
                <TextField
                  fullWidth
                  id="lastName"
                  name="lastName"
                  label={translate('lastName')}
                  placeholder={translate('lastName')}
                  error={!!form.errors.lastName}
                  helperText={form.errors.lastName}
                  value={form.values.lastName}
                  onChange={handleFieldValueChange}
                />
              </GridItem>
            </GridSection>
            <GridSection>
              <GridItem>
                <TextField
                  fullWidth
                  id="phone"
                  name="phone"
                  label={`${translate('phone')}${practitionerTenantData?.requiredPhone ? "*" : ""}`}
                  placeholder={translate('phone')}
                  error={!!form.errors.phone}
                  helperText={form.errors.phone}
                  value={form.values.phone}
                  onChange={handleFieldValueChange}
                />
              </GridItem>
              <GridItem>
                <TextField
                  fullWidth
                  id="email"
                  name="email"
                  label={`${translate('email')}${practitionerTenantData?.requiredEmail ? "*" : ""}`}
                  placeholder={translate('email')}
                  error={!!form.errors.email}
                  helperText={form.errors.email}
                  value={form.values.email}
                  onChange={handleFieldValueChange}
                />
              </GridItem>
            </GridSection>

            <GridSection>
              <GridItem xs={12}>
                <AddressAutoComplete
                  label={`${translate('address')}${practitionerTenantData?.requiredAddress ? "*" : ""}`}
                  address={selectedAddress}
                  onAddressChange={(value) => {
                    setSelectedAddress(value);
                  }}
                  onFocus={() => {
                    setShowAddressFields(true);
                    setShowMap(true);
                  }}
                />
                {/* <TextField
                fullWidth
                id="address"
                name="address"
                onClick={() => {
                  setShowAddressFields(true);
                  setShowMap(true);
                }}                
                label={translate('address')}
                placeholder={translate('address')}
                value={form.values.address}
                onChange={handleFieldValueChange}
              /> */}
              </GridItem>
            </GridSection>

            {showAddressFields && (
              <>
                <GridSection>
                  <GridItem>
                    <TextField
                      fullWidth
                      id="street1"
                      name="street1"
                      label={`${translate('street1')}${practitionerTenantData?.requiredAddress ? "*" : ""}`}
                      placeholder={translate('street1')}
                      error={!!form.errors.street1}
                      helperText={form.errors.street1}
                      value={form.values.street1}
                      onChange={handleFieldValueChange}
                    />
                  </GridItem>
                  <GridItem>
                    <TextField
                      fullWidth
                      id="street2"
                      name="street2"
                      label={translate('street2')}
                      placeholder={translate('street2')}
                      value={form.values.street2}
                      onChange={handleFieldValueChange}
                    />
                  </GridItem>
                </GridSection>
                <GridSection>
                  <GridItem>
                    <TextField
                      fullWidth
                      id="city"
                      name="city"
                      label={`${translate('city')}${practitionerTenantData?.requiredAddress ? "*" : ""}`}
                      placeholder={translate('city')}
                      value={form.values.city}
                      onChange={handleFieldValueChange}
                    />
                  </GridItem>
                  <GridItem>
                    <TextField
                      fullWidth
                      id="state"
                      name="state"
                      label={`${translate('state')}${practitionerTenantData?.requiredAddress ? "*" : ""}`}
                      placeholder={translate('state')}
                      value={form.values.state}
                      onChange={handleFieldValueChange}
                    />
                  </GridItem>
                </GridSection>
                <GridSection>
                  <GridItem>
                    <TextField
                      fullWidth
                      id="zipCode"
                      name="zipCode"
                      label={`${translate('zipCode')}${practitionerTenantData?.requiredAddress ? "*" : ""}`}
                      placeholder={translate('zipCode')}
                      value={form.values.zipCode}
                      onChange={handleFieldValueChange}
                    />
                  </GridItem>
                </GridSection>
              </>
            )}
            <GridSection xs={12}>
              {!!errorMessage && (
                <Box>
                  <Typography
                    color={'#ff3333'}
                    sx={{
                      fontWeight: 'bold',
                      wordBreak: 'normal',
                      whiteSpace: 'pre-line',
                      fontSize: '0.9rem',
                    }}
                  >
                    {errorMessage}
                  </Typography>
                </Box>
              )}
            </GridSection>

            <GridSection>
              <GridItem sx={{ display: 'flex', alignItems: 'center' }}>
                <Tooltip title={translate('useExternalProviderDirectoryInfo')}>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={useExternalProviderDirectory}
                          name="useExternalProviderDirectory"
                          id="useExternalProviderDirectory"
                          color="primary"
                          onChange={handleUseExternalProviderDirectory}
                        />
                      }
                      label={translate('useExternalProviderDirectory')}
                    />
                  </FormGroup>
                </Tooltip>
              </GridItem>
            </GridSection>

            <GridSection>
              <GridItem
                xs={12}
                sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end' }}
              >
                <CancelButton
                  onClick={() => {
                    onClose?.({}, 'backdropClick');
                  }}
                />
                <Tooltip title={translate('search')}>
                  <span>
                    <CustomLoadingButton
                      disabled={isSearching}
                      sx={{
                        marginLeft: 1,
                      }}
                      loading={isSearching}
                      onClick={handleProviderSearch}
                      variant="contained"
                    >
                      {translate('search')}
                    </CustomLoadingButton>
                  </span>
                </Tooltip>

                {!!searchCombinations.length && (
                  <Box sx={{ position: 'relative', top: -4, marginLeft: 1.4 }}>
                    <Tooltip title={translate('possibleSearchCombinations')}>
                      <span>
                        <IconButton
                          className={isInvalidCombinationError ? 'shining-icon-button' : ''}
                          onClick={() => {
                            setIsSearchCombinationDialogOpen(true);
                            setIsInvalidCombinationError(false);
                          }}
                        >
                          <HelpCenterIcon sx={{ height: 30, width: 30 }} color="info" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                )}
              </GridItem>
            </GridSection>
          </Grid>
          <Grid item xs={7} sx={{ marginTop: 0 }}>
            <Box sx={{ height: '100%' }}>
              {showMap ? (
                <Map
                  zoom={5}
                  center={{
                    lat: markerPosition.lat ?? 38.3966908,
                    lng: markerPosition.lng ?? -99.525901,
                  }}
                  isLoaded={isGoogleAPILoaded}
                >
                  {markerPosition.lat !== null && markerPosition.lng !== null ? (
                    <Marker
                      position={{
                        lat: markerPosition.lat,
                        lng: markerPosition.lng,
                      }}
                    />
                  ) : null}
                </Map>
              ) : (
                <>
                  {isDoneSearching ? (
                    !!practitioners.length ? (
                      <Box sx={{ position: 'relative', top: -10 }}>
                        <Typography variant="h6">{translate('searchResults')}</Typography>
                        {checkAclValidation({ acls: [crsAcls.CRS.PRACTITIONER.MANUAL_ADD] }) && (
                          <Box
                            sx={{
                              paddingY: 2,
                              paddingX: 2,
                              backgroundColor: '#cafdf5',
                              marginTop: 2,
                              borderRadius: 1,
                              gap: 12,
                              marginBottom: 2,
                            }}
                          >
                            <Box
                              sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}
                            >
                              <Info htmlColor="#00b8d9" sx={{ marginRight: 1.5 }} />
                              <Typography
                                variant="body2"
                                sx={{ color: '#003768', fontSize: '0.94rem' }}
                              >
                                {translate('resultsMessage')}
                              </Typography>

                              <CreateManuallyButton
                                sx={{ marginLeft: 1.5 }}
                                onClick={handleManualAdd}
                                loading={isAdding}
                                disabled={isAdding}
                              />
                            </Box>
                          </Box>
                        )}
                        {resultsMoreThanLimit && (
                          <Box
                            sx={{
                              paddingY: 2,
                              paddingX: 2,
                              backgroundColor: '#cafdf5',
                              marginTop: 2,
                              borderRadius: 1,
                              gap: 12,
                              marginBottom: 2,
                            }}
                          >
                            <Box
                              sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}
                            >
                              <Warning htmlColor="#00b8d9" sx={{ marginRight: 1.5 }} />
                              <Typography
                                variant="body2"
                                sx={{ color: '#003768', fontSize: '0.94rem' }}
                              >
                                {translate('limitResultsShownMessage')}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                        <DataGrid
                          getRowSpacing={() => ({ bottom: 2, top: 2 })}
                          getRowId={() => uuidv4()}
                          rows={practitioners}
                          columns={gridColumns}
                          className="dataGridWithFilter entityList"
                          sx={[
                            {
                              width: '100%',
                            },
                          ]}
                          // components={{ Footer: () => null, Pagination: () => null }}
                          pageSize={pageSize}
                          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                          rowsPerPageOptions={[10, 25]}
                          isRowSelectable={() => false}
                          autoHeight={true}
                          getRowHeight={() => 'auto'}
                          disableColumnFilter
                          disableColumnMenu
                        />
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'flex-end',
                            marginTop: 3,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontSize: '0.94rem', color: '#919eab' }}
                          >
                            {practitioners.length} {translate('resultsMatch')}
                          </Typography>
                        </Box>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          flexDirection: 'column',
                        }}
                      >
                        <Typography variant="h4" sx={{ textAlign: 'center' }}>
                          {translate('noResultsFound')}
                        </Typography>
                        <Typography variant="body2" sx={{ textAlign: 'center', fontSize: '1rem' }}>
                          {translate('couldntFindPractitioner')}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ textAlign: 'center', fontWeight: 'bold', fontSize: '1rem' }}
                        >
                          {translate('completeFormToCreate')}
                        </Typography>
                        <CreateManuallyButton
                          sx={{ marginTop: 1 }}
                          onClick={handleManualAdd}
                          disabled={isAdding}
                          loading={isAdding}
                        />
                      </Box>
                    )
                  ) : (
                    <Box
                      sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        flexDirection: 'column',
                      }}
                    >
                      <Typography variant="h4" sx={{ textAlign: 'center' }}>
                        {translate('useFieldsOnTheLeft')}
                      </Typography>
                    </Box>
                  )}
                </>
              )}
            </Box>
          </Grid>
        </Grid>
      </CustomModal>
      <SearchCombinationsDialog
        open={isSearchCombinationDialogOpen}
        onClose={() => {
          setIsSearchCombinationDialogOpen(false);
        }}
        searchCombinations={searchCombinations}
      />
      <ExistingPractitionerDialog
        isOpen={isExistingPractitionersDialogOpen}
        practitioners={existingPractitioners}
        onClose={() => {
          setIsExistingPractitionersDialogOpen(false);
        }}
      />
    </>
  );
};

interface CreateManuallyButtonProps extends CustomLoadingButtonProps {}

const CreateManuallyButton = ({ sx, ...buttonProps }: CreateManuallyButtonProps) => {
  const translate = useTranslations('practitioner');
  return (
    <CustomLoadingButton
      sx={[
        {
          whiteSpace: 'nowrap',
          backgroundColor: '#006c9c',
          color: '#ffffff',
          height: 34,
          '&:hover': {
            backgroundColor: '#006c9c',
          },
        },
        ...spreadSxProp(sx),
      ]}
      {...buttonProps}
    >
      {translate('createManually')}
    </CustomLoadingButton>
  );
};

const mapPractitionerToPractitionerGrid = (
  practitioner: WrappedPractitioner | DirectoryProviderLookUp
): PractitionerGrid => {
  if ((practitioner as WrappedPractitioner)?.resourceType) {
    const wrappedPractitioner = practitioner as WrappedPractitioner;
    return {
      id: wrappedPractitioner?.id ?? '',
      firstName: wrappedPractitioner.getFirstName() ?? '',
      middleName: wrappedPractitioner.getMiddleName() ?? '',
      lastName: wrappedPractitioner.getLastName() ?? '',
      npi: wrappedPractitioner.getNPI() ?? '',
      phone: wrappedPractitioner.getPhones()?.[0]?.value ?? '',
      email: wrappedPractitioner.getEmails()?.[0]?.value ?? '',
      address: wrappedPractitioner?.address?.[0] ?? null,
      foundAt: 'localDb',
      gender: wrappedPractitioner?.gender ?? '',
      birthDate: moment(wrappedPractitioner?.birthDate ?? null),
    };
  }
  const provider = practitioner as DirectoryProviderLookUp;
  return {
    id: provider?.uuid ?? '',
    firstName: provider?.first_name?.trim?.() ?? '',
    middleName: provider?.middle_name?.trim?.() ?? '',
    lastName: provider?.last_name?.trim?.() ?? '',
    npi: provider?.npi ?? '',
    phone: provider?.telecom?.find?.(({ type }) => type === 'phone')?.value ?? '',
    email: provider?.telecom?.find?.(({ type }) => type === 'email')?.value ?? '',
    address: provider?.address?.[0] ? mapProviderAddressToFhir(provider?.address?.[0]) : null,
    foundAt: 'directory',
    gender: provider?.gender ? getGenderFromProvider(provider?.gender) : '',
    birthDate: moment(provider?.dob ?? null),
  };
};

export default NewPractitionerModal;
